import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { Search01Icon, Add01Icon, InboxIcon, HourglassIcon, CheckmarkCircle01Icon, FileEditIcon, ViewIcon } from '@hugeicons/core-free-icons';
import { CaseEnquiryStateService } from './services/case-enquiry-state.service';
import { CaseEnquiryService } from '../../core/services/case-enquiry.service';
import { AuthService } from '../../core/services/auth.service';

export interface Enquiry {
    id: string; // Keep ID for tracking
    case_enquiry_id?: number;
    client_name: string; // Client First & Last Name
    injury_type: string; // Basic Injury Type
    incident_date: string; // Date of Incident
    details?: string; // Additional details (optional)
    lien_details?: string; // Additional details of lien issue / status
    lien_amount?: number; // Approximate lien amount
    settlement_status?: string; // Settlement Status
    settlement_amount?: string | number | null; // If Settled
    has_loans?: boolean; // Settlement Loans (yes/no)
    loan_amount?: number; // If yes
    total_medicals?: number; // Total Medicals
    legal_fees?: number; // Legal Fees
    costs?: number; // Costs
    status: string; // Internal Board Status
    inquiries_status?: string; // API status
    attorney_name?: string;
    review_notes?: string;
    has_submission?: number; // 0 = not submitted, 1 = submitted
}

import { DatePickerComponent } from '../../shared/components/date-picker/date-picker.component';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';

@Component({
    selector: 'app-case-enquiry',
    standalone: true,
    imports: [CommonModule, FormsModule, DatePickerComponent, HugeiconsIconComponent, EmptyStateComponent],
    templateUrl: './case-enquiry.component.html',
    styleUrl: './case-enquiry.component.scss'
})
export class CaseEnquiryComponent implements OnInit {

    searchTerm: string = '';
    filterDate: string = '';
    activeTab: string = 'new';

    // Pagination
    currentPage: number = 1;
    itemsPerPage: number = 10;

    // Icons
    searchIcon    = Search01Icon;
    addIcon       = Add01Icon;
    submitCaseIcon = FileEditIcon;
    viewIcon      = ViewIcon;

    enquiries: Enquiry[] = [];

    columns = [
        {
            key: 'new',
            title: 'New',
            noDataTitle: 'No New Inquiries',
            noDataDescription: 'No fresh inquiries found.',
            noDataIcon: InboxIcon
        },
        {
            key: 'accepted',
            title: 'Accepted',
            noDataTitle: 'Nothing in Progress',
            noDataDescription: 'All inquiries are caught up.',
            noDataIcon: HourglassIcon
        },
        {
            key: 'rejected',
            title: 'Rejected',
            noDataTitle: 'No Rejections',
            noDataDescription: 'Clean record for now.',
            noDataIcon: CheckmarkCircle01Icon
        }
    ];

    constructor(
        private router: Router,
        private enquiryState: CaseEnquiryStateService,
        private caseEnquiryService: CaseEnquiryService,
        private authService: AuthService
    ) { }

    get isAdmin(): boolean {
        return this.authService.getRole() === 'ADMIN';
    }

    /** Unique YYYY-MM-DD dates from all enquiries — used to highlight calendar days. */
    get enquiryDates(): string[] {
        const seen = new Set<string>();
        for (const e of this.enquiries) {
            const d = (e.incident_date ?? '').trim().slice(0, 10);
            if (d) seen.add(d);
        }
        return Array.from(seen);
    }

    ngOnInit() {
        this.fetchEnquiries();
    }

    fetchEnquiries() {
        this.caseEnquiryService.getEnquiries().subscribe({
            next: (data) => {
                this.enquiries = data.map((item: any) => ({
                    id: item.case_enquiry_id ? `ENQ-${item.case_enquiry_id}` : 'N/A',
                    case_enquiry_id: item.case_enquiry_id,
                    client_name: item.client_name,
                    injury_type: item.basic_injury_type,
                    incident_date: item.incident_date,
                    settlement_amount: item.settlement_amount,
                    status: 'new', // Placeholder, mapped below
                    inquiries_status: item.inquiries_status,
                    attorney_name: item.attorney_name || 'Unknown',
                    // Map other fields if necessary or keep them optional
                    details: item.details,
                    lien_details: item.lien_details,
                    lien_amount: item.lien_amount,
                    settlement_status: item.settlement_status,
                    has_loans: item.has_loans,
                    loan_amount: item.loan_amount,
                    total_medicals: item.total_medicals,
                    legal_fees: item.legal_fees,
                    costs: item.costs,
                    has_submission: item.has_submission
                }));
            },
            error: () => { }
        });
    }

    navigateToAdd() {
        this.router.navigate(['/case-enquiry/add']);
    }

    /** Returns true when the enquiry has an accepted status */
    isAcceptedStatus(e: Enquiry): boolean {
        const s = (e.inquiries_status ?? '').toLowerCase();
        return s === 'accept' || s === 'accepted';
    }

    getEnquiriesByStatus(statusKey: string) {
        return this.enquiries.filter(e => {
            let matchesStatus = false;

            if (statusKey === 'new') {
                // NEW tab: status is 'New'  OR  accepted but case details NOT yet submitted
                const isNew      = e.inquiries_status === 'New';
                const isAccepted = this.isAcceptedStatus(e);
                const notSubmitted = e.has_submission !== 1;
                matchesStatus = isNew || (isAccepted && notSubmitted);
            } else if (statusKey === 'accepted') {
                // ACCEPTED tab: accepted AND case details already submitted
                matchesStatus = this.isAcceptedStatus(e) && e.has_submission === 1;
            } else if (statusKey === 'rejected') {
                const s = (e.inquiries_status ?? '').toLowerCase();
                matchesStatus = s === 'rejected' || s === 'reject';
            }

            // Search Filter — attorney excluded (same value across all records causes false positives)
            const term = this.searchTerm.toLowerCase().trim();
            const matchesSearch = !term ||
                e.client_name.toLowerCase().includes(term) ||
                e.id.toLowerCase().includes(term) ||
                e.injury_type.toLowerCase().includes(term);

            // Date Filter — compare only the YYYY-MM-DD portion (API may include time)
            const matchesDate = !this.filterDate ||
                (e.incident_date ?? '').trim().startsWith(this.filterDate);

            return matchesStatus && matchesSearch && matchesDate;
        });
    }

    /**
     * True when the enquiry is accepted but case details have NOT been submitted yet.
     * Only non-admin users (attorneys) should see the submit button for these.
     */
    canSubmitCase(enquiry: Enquiry): boolean {
        return !this.isAdmin && this.isAcceptedStatus(enquiry) && enquiry.has_submission !== 1;
    }

    /** Navigate to the Add Case Details page for this enquiry */
    navigateToSubmitCase(enquiry: Enquiry, event: MouseEvent): void {
        event.stopPropagation(); // Prevent row click / details modal from opening
        this.router.navigate(['/cases/add-details'], {
            queryParams: { enquiryId: enquiry.case_enquiry_id }
        });
    }

    getFilteredEnquiries(): Enquiry[] {
        return this.getEnquiriesByStatus(this.activeTab);
    }

    getTotalFilteredEnquiries(): number {
        return this.getEnquiriesByStatus(this.activeTab).length;
    }

    /** Fires on every keystroke in the search box */
    onSearchInput(event: Event): void {
        this.searchTerm = (event.target as HTMLInputElement).value;
        this.currentPage = 1;
    }

    /** Called when date filter changes — resets to page 1 */
    onFilterChange(): void {
        this.currentPage = 1;
    }

    onPageChange(page: number) {
        this.currentPage = page;
    }

    changeTab(tabKey: string) {
        this.activeTab = tabKey;
        this.currentPage = 1; // Reset to first page when changing tabs
    }

    getTabClass(tabKey: string): string {
        if (this.activeTab !== tabKey) {
            return 'tab-inactive';
        }

        switch (tabKey) {
            case 'new':
            case 'accepted':
                return 'tab-primary';
            case 'rejected':
                return 'tab-rejected';
            default:
                return 'tab-primary';
        }
    }

    getActiveColumn() {
        return this.columns.find(c => c.key === this.activeTab) || this.columns[0];
    }

    getStatusClass(status: string | undefined): string {
        if (!status) return '';
        const s = status.toLowerCase();
        if (s === 'new') return 'new';
        if (['accept', 'accepted'].includes(s)) return 'accepted';
        if (['reject', 'rejected'].includes(s)) return 'rejected';
        return '';
    }

    viewDetails(enquiry: Enquiry) {
        const id = enquiry.case_enquiry_id ?? parseInt(enquiry.id.replace('ENQ-', ''), 10);
        if (!id || Number.isNaN(id)) {
            this.enquiryState.selectEnquiry(enquiry);
            return;
        }
        this.caseEnquiryService.getEnquiryById(id).subscribe({
            next: (detail) => {
                const mapped: Enquiry = {
                    id: detail.id ? `ENQ-${detail.id}` : enquiry.id,
                    case_enquiry_id: detail.id ?? enquiry.case_enquiry_id,
                    client_name: [detail.client_first_name, detail.client_last_name].filter(Boolean).join(' ') || enquiry.client_name,
                    injury_type: detail.injury_type ?? enquiry.injury_type,
                    incident_date: detail.date_of_incident ?? enquiry.incident_date,
                    details: detail.details ?? enquiry.details,
                    lien_details: detail.lien_details ?? enquiry.lien_details,
                    lien_amount: detail.lien_amount != null ? parseFloat(detail.lien_amount) : enquiry.lien_amount,
                    settlement_status: detail.settlement_status ?? enquiry.settlement_status,
                    settlement_amount: detail.settlement_amount ?? enquiry.settlement_amount,
                    has_loans: detail.settlement_loans != null ? !!detail.settlement_loans : enquiry.has_loans,
                    loan_amount: detail.loan_amount != null ? parseFloat(detail.loan_amount) : enquiry.loan_amount,
                    total_medicals: detail.total_medicals != null ? parseFloat(detail.total_medicals) : enquiry.total_medicals,
                    legal_fees: detail.legal_fees != null ? parseFloat(detail.legal_fees) : enquiry.legal_fees,
                    costs: detail.costs != null ? parseFloat(detail.costs) : enquiry.costs,
                    status: detail.status ?? enquiry.status,
                    inquiries_status: detail.inquiries_status ?? enquiry.inquiries_status,
                    attorney_name: enquiry.attorney_name,
                    review_notes: detail.review_notes ?? enquiry.review_notes,
                    has_submission: detail.has_submission ?? enquiry.has_submission
                };
                this.enquiryState.selectEnquiry(mapped);
            },
            error: () => {
                this.enquiryState.selectEnquiry(enquiry);
            }
        });
    }
}
